/**
 * LandingFooter - Footer section with links and branding
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LandingFooter = () => {
  const { t } = useTranslation();
  return (
    <footer className="py-12 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Logo & Company Info */}
          <div className="md:col-span-1">
            <a 
              href="https://parachpay.com" 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity mb-4"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img 
                src="/images/parachpay-logo.jpg" 
                alt="ParachPay Logo" 
                width="32"
                height="32"
                className="h-8 w-auto"
              />
              <span className="font-bold text-lg tracking-wide">PARACHPAY</span>
            </a>
            <div className="text-sm text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">ASELU Investment Growth Limited</p>
              <p>(ParachPay)</p>
              <p>{t('home.footer.registeredIn')} · RC1947196</p>
              <p className="mt-3 text-xs">{t('home.footer.europeanOps')}</p>
              <p className="text-xs">Rudolf Steinerstraat 32,</p>
              <p className="text-xs">2037 GC Haarlem, Netherlands</p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-1">
            <h4 className="font-semibold text-sm mb-4">{t('home.footer.quickLinks')}</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <a href="https://parachpay.com" className="hover:text-foreground transition-colors">{t('home.footer.about')}</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">{t('home.footer.contact')}</Link>
              <Link to="/merchant-verification" className="hover:text-foreground transition-colors">{t('home.footer.merchants')}</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">{t('home.footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">{t('home.footer.terms')}</Link>
            </div>
          </div>

          {/* Download ParachPay */}
          <div className="md:col-span-2">
            <h4 className="font-semibold text-sm mb-4">{t('home.footer.download')}</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {t('home.footer.downloadDesc')}
            </p>
            <div className="flex flex-wrap gap-3">
              <a 
                href="https://play.google.com/store/apps/details?id=com.parachpay.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-80">{t('home.why.getItOn')}</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
              <a 
                href="https://www.amazon.com/gp/product/B0H9JVSW7V"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF9900] text-black px-4 py-2 rounded-lg hover:bg-[#e68a00] transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M15.93 17.09c-.18.16-.43.17-.63.06-.89-.74-1.05-1.08-1.54-1.79-1.47 1.5-2.51 1.95-4.42 1.95-2.25 0-4.01-1.39-4.01-4.17 0-2.18 1.17-3.64 2.86-4.38 1.46-.64 3.49-.76 5.04-.93V7.5c0-.66.05-1.41-.33-1.96-.32-.49-.95-.7-1.5-.7-1.02 0-1.93.53-2.15 1.61-.05.24-.25.48-.47.49l-2.6-.28c-.22-.05-.46-.24-.4-.6.6-3.15 3.45-4.1 6-4.1 1.3 0 3 .35 4.03 1.33C17.11 4.55 17 6.18 17 7.95v4.17c0 1.25.52 1.8 1.01 2.48.17.24.21.53 0 .71-.54.45-1.5 1.29-2.08 1.78zm-2.5-5.88v-.83c-1.93 0-3.98.41-3.98 2.68 0 1.16.6 1.94 1.63 1.94 1.14 0 2.08-.86 2.35-1.79z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-80">{t('home.why.availableAt')}</div>
                  <div className="text-sm font-semibold">Amazon Appstore</div>
                </div>
              </a>
              <a 
                href="https://appgallery.huawei.com/app/C118318155"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex rounded-lg hover:opacity-90 transition-opacity"
                data-testid="footer-huawei-appgallery-badge"
              >
                <img src="/badges/huawei-appgallery-badge.png" alt={t('home.badges.huaweiAlt')} className="h-[52px] w-auto" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ParachPay. {t('home.footer.rights')}
            </p>
            <p className="text-xs text-muted-foreground">
              ASELU Investment Growth Limited · {t('home.footer.registeredIn')} · RC1947196
            </p>
          </div>
          <p className="text-xs text-muted-foreground text-center mb-4" data-testid="footer-not-a-bank">
            {t('notices.notABank')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-muted-foreground">
            <span>{t('home.footer.registeredAddress')} 67 Siluko Road, Benin City, Nigeria</span>
            <span className="hidden sm:inline">·</span>
            <span>{t('home.footer.europeanOpsShort')} Rudolf Steinerstraat 32, 2037 GC Haarlem, Netherlands</span>
            <span className="hidden sm:inline">·</span>
            <a href="mailto:info@parachpay.com" className="text-emerald-500 hover:text-emerald-400 transition-colors">
              info@parachpay.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter;
